import { DTOModelService, DTO_MODEL_SERVICE, ETTypeInfos } from "@cervval.js/common-dto/src/dtoModel.service";
import { FieldMarshaller, SQL_CONNECTOR_UNIX_TIMESTAMP_MARSHALLER, SqlDbDefinition } from "@cervval.js/common-sql-core";
import { setStringMarshallers } from "@cervval.js/common-sql-core/src/marshallers/string.marshaller";
import { buildSQLDBDefinitionFromDTOModel } from "@cervval.js/common-sql-core/src/sql.tableDefinition.tools";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { keyof } from "@cervval.js/common/src/tools/object.tools";
import { keys, typeInfos } from "@cervval.js/ts-reflect";
import { BookingFieldTypeName, BookingFieldTypes, registerBookingMarshallers } from "@ehealth_tools/common-booking/src/data/booking.types";
import { MapFieldTypeName, MapFieldTypes } from "@ehealth_tools/common-map/src/map.types";
import { PaymentFieldTypeName, PaymentFieldTypes } from "@ehealth_tools/common-payment/src/data/payment.types";
import { BaseDTO } from "@ehealth_tools/common/src/data/dto/base.dto";
import { FieldTypeName, FieldTypes, fieldTypeConstraints } from "@ehealth_tools/common/src/data/propertyTypes/property.types";
import { AttachmentElementTypes, attachmentTypeInfos } from "../attachment/attachment.model";
import { BookingElementTypes, bookingTypeInfos } from "../booking/booking.model";
import { CommunicationElementTypes, communicationTypeInfos } from "../communication/communication.model";
import { HealthCheckElementTypes, healthCheckTypeInfos } from "../healthCheck/healthCheck.model";
import { HealthCheckFieldTypeName, HealthCheckFieldTypes } from "../healthCheck/healthCheck.types";
import { OnapsElementTypes, onapsTypeInfos } from "../onaps/onaps.model";
import { OnapsFieldTypeName, OnapsFieldTypes, setMarshallersForOnapsFieldTypes } from "../onaps/onaps.types";
import { PaymentElementTypes, paymentTypeInfos } from "../payments/payment.model";
import { ProgramElementTypes, programTypeInfos } from "../program/program.model";
import { ProgramFieldTypeName, ProgramFieldTypes } from "../program/program.types";
import { CoachSpecialties } from "../users/user.definitions";
import { UsersElementTypes, usersTypeInfos } from "../users/user.model";
import { UserFieldTypeName, UserFieldTypes } from "../users/user.types";
import { BigIntFieldMarshaller, BooleanFieldMarshaller, jsonDataFieldMarshaller } from "./dbMarshallers";
import { injectFetchDataDefinitionsInDbDefinition } from "./fetchData.config";
import { ignoreFields } from "./global.tools";
import { LogActionDTO, LogOperationDTO } from "./log.dto";


export type LogElementTypes = {
    "log_actions": LogActionDTO,
    "log_operations": LogOperationDTO
}

/**
 * The model is the combination of several Element definitions
 * An Element represents a Data Type, it can be associated to 
 * - a DTO (Data Transfer Object) interface
 * - a SQL Table Definition
 * - a Form configuration (list of PropertyConfigs)
 */

/** Merge all DTO types used by application */
export type ElementTypes =
    UsersElementTypes & ProgramElementTypes & HealthCheckElementTypes &
    CommunicationElementTypes & LogElementTypes & AttachmentElementTypes &
    PaymentElementTypes & BookingElementTypes & OnapsElementTypes;

export type ElementTypeName = keyof ElementTypes;
export const elementTypeNames: ElementTypeName[] = keys<ElementTypes>();

export const fetchableElementTypeNames = elementTypeNames.filter(v => !["user_permission_categories", "user_permissions"].includes(v));

/** Merge all Table names used by application (ElementTypeName + additional tables) */
export type AppTableName = ElementTypeName;

/** Merge all FieldType used by application */
export type AppFieldType =
    FieldTypes & UserFieldTypes & ProgramFieldTypes & HealthCheckFieldTypes &
    MapFieldTypes<CoachSpecialties> & PaymentFieldTypes & BookingFieldTypes &
    OnapsFieldTypes;

/** Merge all FieldTypeName used by application */
export type AppFieldTypeName =
    FieldTypeName | UserFieldTypeName | ProgramFieldTypeName | HealthCheckFieldTypeName |
    MapFieldTypeName<CoachSpecialties> | PaymentFieldTypeName | BookingFieldTypeName |
    OnapsFieldTypeName;


/** Log type infos */
export const logTypeInfos: ETTypeInfos<LogElementTypes> = {
    "log_actions": {
        keys: keys<LogActionDTO>(),
        typeInfos: typeInfos<LogActionDTO>()
    },
    "log_operations": {
        keys: keys<LogOperationDTO>(),
        typeInfos: typeInfos<LogOperationDTO>(),
        foreignKeys: {
            logActionId: "log_actions"
        }
    }
};

/**
 * Register a DTOModelService for injection
 */
export function registerDTOModelService(): void {
    const dtoModelService = new DTOModelService<ElementTypes, AppFieldType>(
        elementTypeNames,
        keys<AppFieldType>(),
        keyof<BaseDTO<AppTableName>>("id"),
        "ID");
    const typesInfos = {
        ...usersTypeInfos,
        ...programTypeInfos,
        ...healthCheckTypeInfos,
        ...communicationTypeInfos,
        ...logTypeInfos,
        ...attachmentTypeInfos,
        ...paymentTypeInfos,
        ...bookingTypeInfos,
        ...onapsTypeInfos
    };
    ignoreFields(typesInfos, ["__tableName"]);
    dtoModelService.registerTypeInfos(typesInfos);
    injector.register(dtoModelService, { overrideIfDependencyRegistered: true });
}

/**
 * Return the registered DTOModelService for injection.
 * If none were previously registered, one will be automatically created and registered for injection.
 * @returns DTOModelService
 */
export function getDTOModelService(): DTOModelService<ElementTypes, AppFieldType> {
    const serviceAlreadyRegistered = injector.find<DTOModelService<ElementTypes, AppFieldType>>(DTO_MODEL_SERVICE) != null;
    if (!serviceAlreadyRegistered) {
        registerDTOModelService();
    }
    return injector.get<DTOModelService<ElementTypes, AppFieldType>>(DTO_MODEL_SERVICE);
}

/**
 * Build a DbDefinition for SqlHelper
 */
export function getDbDefinition(): SqlDbDefinition<AppTableName, AppFieldTypeName> {
    // configure marshallers
    const marshallers = new Map<AppFieldTypeName, FieldMarshaller<AppFieldType[AppFieldTypeName]> | string>();
    setStringMarshallers(marshallers, fieldTypeConstraints);
    // marshallers.set("ID_ARRAY", SQL_CONNECTOR_NUMBER_ARRAY_TO_CSV_MARSHALLER);
    marshallers.set("UnixTimestamp", SQL_CONNECTOR_UNIX_TIMESTAMP_MARSHALLER);
    marshallers.set("BOOLEAN", BooleanFieldMarshaller);
    marshallers.set("BIGINT", BigIntFieldMarshaller);
    marshallers.set("OBJECT", jsonDataFieldMarshaller);
    marshallers.set("BODY_PARTS", jsonDataFieldMarshaller);
    marshallers.set("MUSCLES", jsonDataFieldMarshaller);
    marshallers.set("EXERCISE_TYPES", jsonDataFieldMarshaller);
    marshallers.set("EXERCISE_EQUIPMENTS", jsonDataFieldMarshaller);
    marshallers.set("DAYS_OF_WEEKS", jsonDataFieldMarshaller);
    marshallers.set("SPECIALTIES", jsonDataFieldMarshaller);
    marshallers.set("PRODUCT_DESCRIPTIONS", jsonDataFieldMarshaller);
    marshallers.set("ID_ARRAY", jsonDataFieldMarshaller);

    setMarshallersForOnapsFieldTypes<AppFieldType>(marshallers);

    registerBookingMarshallers<AppFieldType>(marshallers);
    // build from DTO model
    const dbDefinition = buildSQLDBDefinitionFromDTOModel(marshallers, injector.get<DTOModelService<ElementTypes, AppFieldType>>(DTO_MODEL_SERVICE));
    injectFetchDataDefinitionsInDbDefinition(dbDefinition);
    return dbDefinition;
}
